import React, { Suspense, useEffect, useRef, useState, lazy } from "react";
import { useSearchParams } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import {
  RectangleStackIcon,
  PencilSquareIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import MagneticButton from "../components/motion/MagneticButton";
import Reveal from "../components/motion/Reveal";

gsap.registerPlugin(ScrollTrigger);

const HeroScene = lazy(() => import("../components/HeroScene"));

const DISCORD_INVITE = "https://discord.gg/v2TzrDz5Kz";

const skipMotion = () =>
  typeof window !== "undefined" &&
  (navigator.webdriver ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches);

const features = [
  {
    name: "Customize Everything",
    description:
      "Personalize every pixel of your presence — profile image, colors, description, birthday, badges and full background art on both your profile and rank card.",
    icon: PencilSquareIcon,
    accent: "from-reored to-rose-500",
  },
  {
    name: "Battle Pass",
    description:
      "Level up to unlock seasonal rewards: exclusive profile cards, rank skins, badges and currency. Every season brings a new theme to chase.",
    icon: RectangleStackIcon,
    accent: "from-amber-500 to-reored",
  },
  {
    name: "Ranking & Leaderboards",
    description:
      "Earn XP for every message, climb the global leaderboard and flex your rank with cinematic animated rank cards rendered as video.",
    icon: ArrowTrendingUpIcon,
    accent: "from-reoviolet to-fuchsia-500",
  },
  {
    name: "Subarashi Ecosystem",
    description:
      "Reo is part of a living ecosystem — economy, roleplay, marketplace and more, all connected and constantly updated.",
    icon: UserGroupIcon,
    accent: "from-sky-500 to-reoviolet",
  },
];

const marqueeWords = [
  "LEVEL UP", "CUSTOMIZE", "COMPETE", "BATTLE PASS", "RANK UP",
  "COLLECT BADGES", "ROLEPLAY", "ECONOMY",
];

const LoginFailedToast = () => {
  const [params, setParams] = useSearchParams();
  const [visible, setVisible] = useState(params.get("login") === "failed");

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => {
      setVisible(false);
      params.delete("login");
      setParams(params, { replace: true });
    }, 6000);
    return () => clearTimeout(t);
  }, [visible, params, setParams]);

  if (!visible) return null;
  return (
    <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 glass border-reored/40 px-6 py-4 animate-slide-up">
      <p className="font-poppins text-sm text-white">
        ❌ Discord login failed or was cancelled — please try again.
      </p>
    </div>
  );
};

const Home = () => {
  const root = useRef(null);

  useGSAP(() => {
    if (skipMotion()) return;

    // Hero entrance
    gsap
      .timeline({ defaults: { ease: "power3.out" } })
      .from("[data-hero-line]", { yPercent: 110, opacity: 0, duration: 1, stagger: 0.12 })
      .from("[data-hero-sub]", { y: 30, opacity: 0, duration: 0.8 }, "-=0.5")
      .from("[data-hero-cta]", { y: 20, opacity: 0, duration: 0.6 }, "-=0.4")
      .from("[data-hero-canvas]", { opacity: 0, duration: 1.2 }, "-=0.8");

    // Subtle parallax on the glow blobs
    gsap.utils.toArray("[data-parallax]").forEach((el) => {
      gsap.to(el, {
        yPercent: 30,
        ease: "none",
        scrollTrigger: { trigger: root.current, start: "top top", end: "bottom top", scrub: 1 },
      });
    });

    // Hero copy and character drift apart at different speeds while
    // scrolling out of the hero — gives the text a parallax depth
    gsap.utils.toArray("[data-depth]").forEach((el) => {
      gsap.to(el, {
        yPercent: Number(el.dataset.depth) * -100,
        ease: "none",
        scrollTrigger: {
          trigger: "[data-hero]",
          start: "top top",
          end: "bottom top",
          scrub: 0.6,
        },
      });
    });

    // Cursor parallax: hero layers drift gently toward the pointer
    const hero = root.current.querySelector("[data-hero]");
    const movers = gsap.utils.toArray("[data-mouse-depth]").map((el) => ({
      f: parseFloat(el.dataset.mouseDepth) || 10,
      xTo: gsap.quickTo(el, "x", { duration: 0.9, ease: "power3.out" }),
      yTo: gsap.quickTo(el, "y", { duration: 0.9, ease: "power3.out" }),
    }));
    const onHeroMove = (e) => {
      const cx = (e.clientX / window.innerWidth - 0.5) * 2;
      const cy = (e.clientY / window.innerHeight - 0.5) * 2;
      movers.forEach((m) => {
        m.xTo(cx * m.f);
        m.yTo(cy * m.f);
      });
    };
    hero?.addEventListener("mousemove", onHeroMove);
    return () => hero?.removeEventListener("mousemove", onHeroMove);

    // Big section titles slide in as you scroll. IntersectionObserver +
    // timeout failsafe — headings can never end up stuck invisible.
    gsap.utils.toArray("[data-title]").forEach((el) => {
      gsap.set(el, { y: 80, opacity: 0 });
      let done = false;
      const show = () => {
        if (done) return;
        done = true;
        gsap.to(el, { y: 0, opacity: 1, duration: 1, ease: "power3.out", overwrite: true });
      };
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          show();
          io.disconnect();
        }
      }, { rootMargin: "0px 0px -10% 0px" });
      io.observe(el);
      setTimeout(show, 4000);
    });
  }, { scope: root });

  return (
    <div ref={root} className="overflow-hidden">
      <LoginFailedToast />

      {/* ============ HERO ============ */}
      <section data-hero className="relative min-h-screen flex items-center">
        {/* Glow blobs */}
        <div data-parallax data-mouse-depth="28" className="absolute -top-32 -left-32 w-[34rem] h-[34rem] rounded-full glow-red blur-3xl" />
        <div data-parallax data-mouse-depth="40" className="absolute top-1/3 -right-40 w-[40rem] h-[40rem] rounded-full glow-violet blur-3xl" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 items-center gap-8 py-20 lg:py-0">
          {/* Copy */}
          <div data-depth="0.25" data-mouse-depth="8" className="text-center lg:text-left order-2 lg:order-1">
            <div className="overflow-hidden">
              <p data-hero-line className="font-poppins text-sm sm:text-base tracking-[0.35em] text-white/50 uppercase mb-4">
                The Discord bot that levels you up
              </p>
            </div>
            <h1 className="font-Bebas display-hero">
              <span className="block overflow-hidden"><span data-hero-line className="block text-white">HI,</span></span>
              <span className="block overflow-hidden"><span data-hero-line className="block text-gradient">I'M REO</span></span>
            </h1>
            <div data-hero-line className="flex items-center justify-center lg:justify-start gap-4 mt-3">
              <span className="font-Bebas text-gradient text-[clamp(2.2rem,5vw,3.8rem)] leading-none tracking-[0.25em] animate-floaty">
                レオ
              </span>
              <span className="hidden sm:block h-px w-20 bg-gradient-to-r from-reored/80 to-transparent" />
              <span className="font-poppins text-white/40 text-xs sm:text-sm tracking-[0.35em] uppercase">
                "REO" in Japanese
              </span>
            </div>
            <p data-hero-sub className="font-poppins text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mx-auto lg:mx-0 mt-6 mb-10">
              Climb the leaderboard, earn battle pass rewards, customize your
              profile down to the last pixel — and show it all off with
              cinematic animated cards, right inside Discord.
            </p>
            <div data-hero-cta className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <MagneticButton
                className="px-8 py-4 rounded-full bg-reored text-white font-poppins font-semibold text-base sm:text-lg shadow-[0_0_25px_rgba(213,60,53,0.25)] hover:shadow-[0_0_35px_rgba(213,60,53,0.4)] transition-shadow duration-300"
                onClick={() => window.open(DISCORD_INVITE)}
              >
                Add Reo to Discord →
              </MagneticButton>
              <a
                href="/battlepass"
                className="px-8 py-4 rounded-full glass glass-hover font-poppins text-white/80 text-base sm:text-lg"
              >
                Explore Battle Pass
              </a>
            </div>
          </div>

          {/* 3D model */}
          <div data-hero-canvas data-depth="0.1" data-mouse-depth="14" className="order-1 lg:order-2 h-[320px] xs:h-[380px] sm:h-[460px] lg:h-[80vh] w-full">
            <Suspense
              fallback={
                <div className="h-full w-full flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full border-2 border-reored border-t-transparent animate-spin" />
                </div>
              }
            >
              <HeroScene />
            </Suspense>
          </div>
        </div>

        {/* Scroll hint */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/30">
          <span className="font-poppins text-xs tracking-[0.3em] uppercase">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent animate-pulse-glow" />
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section className="relative py-6 border-y border-white/10 bg-voidlight/60 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...marqueeWords, ...marqueeWords].map((word, i) => (
            <span key={i} className="font-Bebas text-2xl sm:text-3xl mx-6 sm:mx-8 text-white/25">
              {word} <span className="text-reored/60 ml-12 sm:ml-16">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 data-title className="font-Bebas display-section text-white mb-4">
            BUILT TO MAKE YOUR SERVER <span className="text-gradient">ALIVE</span>
          </h2>
          <p className="font-poppins text-white/50 max-w-2xl text-base sm:text-lg mb-14 sm:mb-20">
            Battle pass, roleplay and a full economy — integrated with the
            Subarashi ecosystem for an experience that grows every season.
          </p>

          <Reveal className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7">
            {features.map((feature) => (
              <div key={feature.name} data-reveal className="group glass glass-hover p-6 sm:p-8 relative overflow-hidden">
                <div className={`absolute -top-16 -right-16 w-44 h-44 rounded-full bg-gradient-to-br ${feature.accent} opacity-10 blur-2xl group-hover:opacity-25 transition-opacity duration-500`} />
                <div className={`inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${feature.accent} mb-5 shadow-lg`}>
                  <feature.icon className="h-7 w-7 text-white" aria-hidden="true" />
                </div>
                <h3 className="font-Bebas text-2xl sm:text-3xl text-white mb-3 tracking-wide">
                  {feature.name}
                </h3>
                <p className="font-poppins text-white/55 text-sm sm:text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="relative py-24 sm:py-32 bg-voidlight/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 data-title className="font-Bebas display-section text-white mb-14 sm:mb-20">
            THREE STEPS TO <span className="text-gradient">GLORY</span>
          </h2>
          <Reveal className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" stagger={0.18}>
            {[
              { n: "01", title: "Chat & Earn XP", text: "Every message earns points — double on weekends. No grinding menus, just be part of your community." },
              { n: "02", title: "Level Up & Unlock", text: "Levels unlock battle pass tiers packed with profile cards, badges, rank skins and RP currency." },
              { n: "03", title: "Flex Your Card", text: "Run /rank or /profile and Reo renders your customized card as a cinematic animated video." },
            ].map((step) => (
              <div key={step.n} data-reveal className="relative glass p-8 sm:p-10">
                <span className="font-Bebas text-7xl sm:text-8xl text-white/5 absolute top-3 right-5 select-none">
                  {step.n}
                </span>
                <span className="font-Bebas text-xl text-reored tracking-[0.2em]">{step.n}</span>
                <h3 className="font-Bebas text-3xl text-white mt-2 mb-4 tracking-wide">{step.title}</h3>
                <p className="font-poppins text-white/55 text-sm sm:text-base leading-relaxed">{step.text}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative py-28 sm:py-40 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[50rem] h-[50rem] rounded-full glow-red blur-3xl opacity-40" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h2 data-title className="font-Bebas display-section text-white mb-6">
            READY TO <span className="text-gradient">LEVEL UP</span> YOUR SERVER?
          </h2>
          <p className="font-poppins text-white/55 text-base sm:text-lg mb-10 max-w-xl mx-auto">
            Join the community, invite Reo and start your climb today.
          </p>
          <MagneticButton
            className="px-10 py-5 rounded-full bg-white text-void font-poppins font-bold text-lg hover:bg-white/90 transition-colors duration-300 shadow-[0_0_30px_rgba(255,255,255,0.12)]"
            onClick={() => window.open(DISCORD_INVITE)}
          >
            Join the Discord ✦
          </MagneticButton>
        </div>
      </section>
    </div>
  );
};

export default Home;
